import { useQuery } from '@tanstack/react-query';
import NextLink from 'next/link';
import PropTypes from 'prop-types';

import Icon from 'shopper/components/Icon';
import Text from 'shopper/components/Text';

import Anchor from 'components/Anchor';
import UserAvatar from 'components/UserAvatar';

import { toFormattedCommentCount } from 'lib/comments';
import { formatDateByDayAbbreviatedMonthFullYear } from 'lib/date';
import { toUserProfileLink } from 'lib/links';
import { commentQueryKeys } from 'lib/queryKeys';

import { getArticleCommentsCount } from 'services/comment';

import ArticleSocialMediaShare from './SocialMediaShare';

const ArticleInfo = ({
  articleId,
  articlePublished,
  articleTitle,
  gaEventCategory,
  userName,
  userPhoto,
  userUsername,
}) => {
  const { data: commentCount } = useQuery({
    queryKey: commentQueryKeys.article.count({ articleId }),
    queryFn: ({ signal }) => getArticleCommentsCount({ articleId }, { signal }),
    enabled: false,
  });

  return (
    <div className="mt-4 flex flex-col items-center justify-between dark:border-neutral-low-300 md:mt-6 md:flex-row md:border-x-0 md:border-y md:border-neutral-high-300">
      <div className="flex w-full items-center justify-center border-y border-neutral-high-300 py-2 dark:border-neutral-low-300 md:w-max md:justify-between md:border-0">
        <div className="flex items-center">
          <div className="mr-2 flex items-center">
            <NextLink
              href={toUserProfileLink(userUsername)}
              prefetch={false}
              passHref
            >
              <Anchor>
                <UserAvatar
                  size="size4"
                  userName={userName}
                  userPhoto={userPhoto}
                />
              </Anchor>
            </NextLink>
            <Text className="ml-2 line-clamp-1" size="size2">
              {userName}
            </Text>
          </div>
          •
          <div className="mx-2 flex items-center">
            <Icon
              className="mr-1 hidden md:block"
              name="clock-filled"
              size="size3"
            />
            <Text className="whitespace-nowrap" size="size2">
              {formatDateByDayAbbreviatedMonthFullYear(articlePublished)}
            </Text>
          </div>
          •
          <div className="mx-2 flex items-center">
            <Icon
              className="mr-1 hidden md:block"
              name="messages"
              size="size3"
            />
            <Text className="whitespace-nowrap" size="size2">
              {toFormattedCommentCount(commentCount)}
            </Text>
          </div>
        </div>
      </div>
      <ArticleSocialMediaShare
        articleTitle={articleTitle}
        className="hidden md:flex"
        gaEventCategory={gaEventCategory}
      />
    </div>
  );
};

ArticleInfo.propTypes = {
  articleId: PropTypes.number.isRequired,
  articlePublished: PropTypes.string.isRequired,
  articleTitle: PropTypes.string.isRequired,
  gaEventCategory: PropTypes.string.isRequired,
  userName: PropTypes.string.isRequired,
  userPhoto: PropTypes.string.isRequired,
  userUsername: PropTypes.string.isRequired,
};

export default ArticleInfo;
